import { useEffect, useRef } from 'react';

interface MetaConfig {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  noindex?: boolean;
}

interface SchemaOrgConfig {
  organization?: boolean;
  website?: boolean;
  webpage?: boolean;
  breadcrumb?: Array<{ name: string; url?: string }>;
  article?: {
    headline: string;
    description: string;
    datePublished: string;
    author: string;
    image?: string;
  };
  faq?: Array<{ question: string; answer: string }>;
}

export function usePageMeta(meta: MetaConfig) {
  const initializedRef = useRef(false);

  useEffect(() => {
    document.title = meta.title;

    const setMetaTag = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMetaTag('description', meta.description);
    setMetaTag('keywords', meta.keywords);
    setMetaTag('og:title', meta.ogTitle || meta.title, true);
    setMetaTag('og:description', meta.ogDescription || meta.description, true);
    setMetaTag('og:type', meta.ogType || 'website', true);
    setMetaTag('og:url', meta.ogUrl || meta.canonicalUrl, true);
    setMetaTag('og:locale', 'zh_CN', true);
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', meta.ogTitle || meta.title);
    setMetaTag('twitter:description', meta.ogDescription || meta.description);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = meta.canonicalUrl;

    let robotsEl = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (meta.noindex) {
      if (!robotsEl) {
        robotsEl = document.createElement('meta');
        robotsEl.name = 'robots';
        document.head.appendChild(robotsEl);
      }
      robotsEl.content = 'noindex, follow';
    } else if (robotsEl) {
      robotsEl.content = 'index, follow';
    }

    initializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.title, meta.description, meta.keywords, meta.canonicalUrl]);
}

export function useSchemaOrg(config: SchemaOrgConfig) {
  useEffect(() => {
    const schemas: Record<string, unknown>[] = [];

    if (config.organization) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '美丽的小蜜桃',
        url: 'https://meilixiaomitao.com/',
        description: '美丽的小蜜桃，精选优质产区水蜜桃，外形饱满圆润，口感鲜嫩多汁甜蜜清香。精品桃子产地直发。',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+86-400-888-6666',
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      });
    }

    if (config.website) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: '美丽的小蜜桃',
        url: 'https://meilixiaomitao.com/',
        description: '美丽的小蜜桃，精选优质产区水蜜桃，鲜嫩多汁甜蜜清香。',
      });
    }

    if (config.webpage) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: document.title,
        description: (document.querySelector('meta[name="description"]') as HTMLMetaElement)?.content || '',
      });
    }

    if (config.breadcrumb && config.breadcrumb.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: config.breadcrumb.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url || undefined,
        })),
      });
    }

    if (config.article) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: config.article.headline,
        description: config.article.description,
        datePublished: config.article.datePublished,
        author: {
          '@type': 'Person',
          name: config.article.author,
        },
        image: config.article.image,
        publisher: {
          '@type': 'Organization',
          name: '美丽的小蜜桃',
        },
      });
    }

    if (config.faq && config.faq.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: config.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      });
    }

    const existingScripts = document.querySelectorAll('script[data-schema="dynamic"]');
    existingScripts.forEach((s) => s.remove());

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'dynamic');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);
}